import { ApolloError } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { Formik, Field, FormikHelpers, FormikProps } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  TouchableOpacity,
  Platform,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { InferType } from 'yup';

import Avatar from '@components/Avatar/Avatar';
import { SkillSelect } from '@components/FormModals/SkillSelect.web';
import { LocationSearch } from '@components/LocationSearch/LocationSearch.web';
import { WebModal } from '@components/Modals/WebModal.web';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import PhoneField from '@components/shared/PhoneField/PhoneField';
import { Spinner } from '@components/shared/Spinner';
import { Tag } from '@components/shared/Tags/Tag';
import TextField from '@components/shared/TextField/TextField';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { PopupTimeZoneButtonWeb } from '@components/Web/PopupTimeZoneButton.web';
import { useUpdateMeMutation } from '@graphql/generated';
import useFileProcessor from '@hooks/useFileProcessor';
import useMe from '@hooks/useMe';
import { useSkills } from '@hooks/useSkills';
import saveProfileSchema from '@schemas/saveProfileSchema';
import { AvatarCachePrefix } from '@src/constants/constants';
import theme from '@themes/theme';
import { useFilePicker } from '@utils/filePicker';
import { guessTimeZone, humanTimeZoneName } from '@utils/formatters/date';
import { getApiConfig } from '@utils/getApiConfig';
import { getApolloErrorMessage } from '@utils/getApolloErrorMessage';
import { getFieldErrors, mutationFailed } from '@utils/graphqlError';
import { AsyncStorage } from '@utils/storage';

export type SaveProfileFormValues = InferType<typeof saveProfileSchema>;

// TA-10140: maps backend field_errors keys (snake_case) onto this form's
// Formik field names (camelCase). Extracted as a pure function so the
// mapping can be unit-tested without mounting the mutation hook/Formik tree.
const PROFILE_FORM_FIELD_NAME_MAP: Record<string, keyof SaveProfileFormValues> =
  {
    name: 'name',
    email: 'email',
    phone_number: 'phoneNumber',
    time_zone: 'timeZone',
  };

export const getProfileFormFieldErrors = (
  error: Parameters<typeof getFieldErrors>[0]
): Partial<Record<keyof SaveProfileFormValues, string>> => {
  const fieldErrors = getFieldErrors(error);
  const result: Partial<Record<keyof SaveProfileFormValues, string>> = {};
  Object.entries(fieldErrors).forEach(([backendField, messages]) => {
    const formikField = PROFILE_FORM_FIELD_NAME_MAP[backendField];
    if (formikField && messages?.[0]) {
      result[formikField] = messages[0];
    }
  });
  return result;
};

const apiConfig = getApiConfig();
// Matches Avatar's own circular-radius convention (width / 2, see
// Avatar.tsx) rather than a themed borderRadii token — a true circle at an
// arbitrary pixel size isn't expressible on that discrete token scale.
const AVATAR_SIZE = 94;

const NewEditProfile: React.FC = () => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const { launchImageSelection } = useFilePicker();
  const { me } = useMe();
  const { skills } = useSkills();
  const [overrideSaveAlert, setOverrideSaveAlert] = useState(false);
  const [skillsModalOpen, setSkillsModalOpen] = useState<boolean>(false);
  const [locationModalOpen, setLocationModalOpen] = useState<boolean>(false);

  const { processFiles } = useFileProcessor();
  const [updateMe] = useUpdateMeMutation();

  const { setIsEditProfileOpen } = useWebDrawer();
  const hasPhoneNumber = !!me?.phoneNumber?.trim();
  const verifyPhoneNumberAvailable = async (phoneNumber: string) => {
    const authEndpoint = `${apiConfig.apiUrl}/api/v2/phone_number_available.json`;
    const response = await fetch(authEndpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        phone_number: phoneNumber,
      }),
    });

    return response.json();
  };

  // TA-10140: maps backend field_errors keys (snake_case) to this form's
  // Formik field names (camelCase). Non-field errors fall back to an alert
  // built from getApolloErrorMessage — replaces the previous `alert(e)` on
  // the raw ApolloError object.
  const applyUpdateMeError = (
    error: ApolloError,
    setFieldError: FormikHelpers<SaveProfileFormValues>['setFieldError']
  ) => {
    const mappedFieldErrors = getProfileFormFieldErrors(error);
    const mappedEntries = Object.entries(mappedFieldErrors);
    mappedEntries.forEach(([field, message]) =>
      setFieldError(field as keyof SaveProfileFormValues, message)
    );
    // Bug fix: alert whenever any backend field_errors key had no Formik
    // mapping (or there simply were none), not only when nothing at all was
    // mapped — otherwise a response mixing a recognized field error with an
    // unrecognized one silently drops the unrecognized one.
    const backendFieldCount = Object.keys(getFieldErrors(error)).length;
    if (
      mappedEntries.length < backendFieldCount ||
      mappedEntries.length === 0
    ) {
      Alert.alert(t('shared:error'), getApolloErrorMessage(error));
    }
  };

  const onSubmit = async (
    values: SaveProfileFormValues,
    { setFieldError }: FormikHelpers<SaveProfileFormValues>
  ) => {
    const {
      name,
      phoneNumber: phoneNumberRaw,
      skillIds: skillIdsRaw,
      location,
      timeZone,
    } = values;
    const { address, latitude, longitude } = location || {
      address: undefined,
      latitude: undefined,
      longitude: undefined,
    };
    const skillIds = skillIdsRaw.filter((s) => s) as string[];

    if (!hasPhoneNumber && phoneNumberRaw) {
      const response = await verifyPhoneNumberAvailable(phoneNumberRaw);

      if (response?.error) {
        setFieldError('phoneNumber', response.error);
        return;
      }
    }

    if (values.avatar && me) {
      const { clientId, contentType, isImage, size, duration, cdnBaseUrl } =
        values.avatar;
      // avatar is a local file at this point
      const doc: Document = {
        __typename: 'Document',
        id: 'avatar',
        owner: me,
        name,
        clientId,
        contentType,
        isImage,
        createdAt: new Date().toISOString(),
        size,
        file: {
          __typename: 'File',
          id: new Date().toISOString(),
          cdnBaseUrl,
          url: '',
          path: '',
          ...values.avatar,
        },
        isAudio: false,
        duration,
      };
      const documents = [doc];

      try {
        const response = await processFiles(documents);
        if (values.avatar?.id) {
          AsyncStorage.deleteItem(AvatarCachePrefix + values.avatar?.id);
        }
        const { blobId } = response[0];
        const result = await updateMe({
          variables: {
            attributes: {
              name,
              phoneNumber: phoneNumberRaw ?? '',
              timeZone,
              skillIds,
              avatarBlobId: blobId,
              address,
              latitude,
              longitude,
            },
          },
          onError: (e) => applyUpdateMeError(e, setFieldError),
          // Code review fix: applyUpdateMeError already shows a field error
          // or a generic Alert — opt out of the global PERMISSION_DENIED/
          // CONFLICT toast so this doesn't double up.
          context: { suppressGlobalMutationErrorToast: true },
        });
        // Code review fix: under the client's global errorPolicy: 'all',
        // updateMe resolves (doesn't reject) even on a GraphQL business
        // error — onError only handles the field-error UI, it doesn't stop
        // this function from continuing as if the save succeeded. Only
        // close the editor when the mutation actually came back clean.
        // `mutationFailed` (not `errors?.length`) because a network-level
        // failure resolves with `errors` as an ApolloError object, which has
        // no `.length` — that read closed the editor and discarded the
        // user's unsaved input on every offline/5xx save.
        if (!mutationFailed(result)) {
          setIsEditProfileOpen(false);
        }
      } catch (err) {
        // Code review fix: this catch only ever sees non-Apollo failures now
        // (processFiles/AsyncStorage), since updateMe's onError above
        // handles GraphQL errors without rejecting — force-casting err to
        // ApolloError could show a blank message for those. Fall back to a
        // plain message instead of assuming ApolloError shape.
        const message =
          err instanceof Error && err.message
            ? err.message
            : t('shared:exceptionMsg');
        Alert.alert(t('shared:error'), message);
      }
    } else {
      const result = await updateMe({
        variables: {
          attributes: {
            name,
            phoneNumber: phoneNumberRaw ?? '',
            timeZone,
            skillIds,
            avatarBlobId: '',
            address,
            latitude,
            longitude,
          },
        },
        onError: (e) => applyUpdateMeError(e, setFieldError),
        // Code review fix: applyUpdateMeError already shows a field error
        // or a generic Alert — opt out of the global PERMISSION_DENIED/
        // CONFLICT toast so this doesn't double up.
        context: { suppressGlobalMutationErrorToast: true },
      });
      // Code review fix: see comment above — only close on a clean result.
      if (!mutationFailed(result)) {
        setIsEditProfileOpen(false);
      }
    }
  };

  const initialValues: SaveProfileFormValues = {
    avatar: null,
    name: me?.name || '',
    email: me?.email || '',
    phoneNumber: me?.phoneNumber || '',
    timeZone: me?.timeZone || guessTimeZone(),
    location: me?.address
      ? {
          latitude: me?.latitude || 0,
          longitude: me?.longitude || 0,
          address: me.address,
        }
      : null,
    skillIds: me?.skills?.map((item) => item.id) || [],
  };

  const createButton = (props: FormikProps<SaveProfileFormValues>) => {
    const { isValid, isSubmitting, touched, submitForm } = props;

    return (
      <Button
        disabled={!isValid || Object.entries(touched).length < 1}
        isLoading={isSubmitting}
        variant='webprimary'
        isSmall
        onPress={() => {
          submitForm();
        }}
        accessibilityLabel='edit profile'>
        {t('models:contacts.save')}
      </Button>
    );
  };

  const ProfileComponent = (props: FormikProps<SaveProfileFormValues>) => {
    const { values, touched, setTouched, setFieldValue, isSubmitting } = props;
    const { avatar, name } = me || {};
    const { skillIds, avatar: newAvatar, location } = values;
    const isUploadingAvatar = isSubmitting && !!newAvatar;

    const openGallery = async () => {
      const r = await launchImageSelection();
      if (r) {
        setFieldValue('avatar', r[0]);
        setTouched({ ...touched, avatar: true });
      }
    };

    const saveAlert = () => {
      Alert.alert(
        t('models:saveProject.title'),
        t('models:saveProject.message'),
        [
          {
            text: t('shared:cancel'),
          },
          {
            text: t('shared:leavePage'),

            onPress: () => {
              setOverrideSaveAlert(true);
              if (touched.avatar) {
                values.avatar = null;
              }
              if (touched.name) {
                values.name = me?.name ?? '';
              }
              if (touched.phoneNumber) {
                values.phoneNumber = me?.phoneNumber ?? '';
              }
              if (touched.location) {
                values.location = '';
              }
              if (touched.skillIds) {
                values.skillIds = me?.skills?.map((item) => item.id) || [];
              }
              // debounce, else alert will show twice
              setTimeout(() => {
                setTouched({});
                navigation.goBack();
              }, 10);
            },
          },
        ]
      );
    };

    const setTimeZone = (value: string) => {
      setTouched({ ...touched, timeZone: true });
      setFieldValue('timeZone', value);
    };

    // Retain the beforeRemove listener to warn the user if they have unsaved changes.
    useEffect(() => {
      const unsubscribe = navigation.addListener('beforeRemove', (e) => {
        if (overrideSaveAlert) {
          return;
        }
        if (Object.entries(touched).length > 0) {
          e.preventDefault();
          saveAlert();
        }
      });
      return unsubscribe;
    }, [navigation, touched, overrideSaveAlert]);

    // Offset configuration for the popup time zone button.
    const offsetPopButton: Partial<any> = {
      position: 'right',
      offset: [0, 6],
    };

    return (
      <Box>
        <Box width={507}>
          <Box
            marginBottom='m'
            alignItems='center'
            flexDirection='row'
            justifyContent='center'
            alignSelf='center'>
            <Box marginEnd='s'>
              <Avatar
                avatar={newAvatar ? newAvatar : avatar}
                width={AVATAR_SIZE}
                height={AVATAR_SIZE}
                label={name}
              />
              {isUploadingAvatar && (
                <Box
                  testID='avatar-upload-spinner'
                  style={[
                    StyleSheet.absoluteFill,
                    { borderRadius: AVATAR_SIZE / 2 },
                  ]}
                  overflow='hidden'
                  alignItems='center'
                  justifyContent='center'
                  backgroundColor='scrimDark50'>
                  <Spinner
                    size={24}
                    strokeWidth={2}
                    color={theme.colors.white}
                  />
                </Box>
              )}
            </Box>
            <TouchableOpacity
              disabled={isSubmitting}
              onPress={openGallery}
              accessibilityLabel={t('models:saveProfile.editPhoto')}>
              <Text marginTop='m' color='greenSecondary' variant='webSmall'>
                {t('models:saveProfile.editPhoto')}
              </Text>
            </TouchableOpacity>
          </Box>

          <Field
            component={TextField}
            label='Name'
            name='name'
            accessibilityLabel={t('models:saveProfile.name')}
            placeholder={t('models:saveProfile.name')}
            textInputProps={{ editable: true }}
          />

          <Text color='textPrimary' variant='webSmall'>
            {t('models:saveProfile.location')}
          </Text>
          <TouchableOpacity
            accessibilityLabel={t('models:saveProfile.location')}
            onPress={() => setLocationModalOpen(true)}>
            <Box
              flexDirection='row'
              borderColor='grey02'
              borderWidth={1}
              height={48}
              alignItems='center'
              borderRadius='xs'
              paddingHorizontal='m'
              mt='xs'
              mb='m'>
              {location && (
                <Text
                  variant='bodySecondary'
                  color='black'
                  numberOfLines={1}
                  mr='m'>
                  {location.address}
                </Text>
              )}
              <Box flex={1} />
              <Icon name='MapPin' variant='l' color='black' />
            </Box>
          </TouchableOpacity>

          {hasPhoneNumber ? (
            <Box height={74} mb='m'>
              <Text
                mb='xs'
                pointerEvents='none'
                variant='labelSmall'
                color='textPrimary'>
                Phone Number
              </Text>
              <Box
                height={48}
                justifyContent='center'
                borderRadius='xs'
                backgroundColor='grey01'
                borderWidth={1}
                borderColor='grey02'>
                <Text
                  pointerEvents='none'
                  variant='bodySecondary'
                  color='textPrimary'
                  ml='m'>
                  {t('format:phoneNumber', { val: values.phoneNumber })}
                </Text>
              </Box>
            </Box>
          ) : (
            <Field
              component={PhoneField}
              label='Phone Number'
              name='phoneNumber'
              placeholder={t('models:saveProfile.phoneNumberPlaceholder')}
              isLarge
              accessibilityLabel={t('models:saveProfile.phoneNumber')}
              phoneIn
              value={values.phoneNumber}
            />
          )}

          <Field
            component={TextField}
            label='Email'
            name='email'
            accessibilityLabel={t('models:saveProfile.email')}
            placeholder={t('models:saveProfile.email')}
            customBorderColor={theme.colors.grey02}
            customBackgroundColor={theme.colors.grey01}
            textInputProps={{ editable: false }}
          />

          <Text color='textPrimary' variant='webSmall' marginBottom='xs'>
            {t('models:saveProfile.timeZone')}
          </Text>
          <PopupTimeZoneButtonWeb
            title={humanTimeZoneName(values.timeZone ?? '') ?? ''}
            setTimeZone={setTimeZone}
            boxProps={offsetPopButton}
            width={602}
            maxHeight={150}
          />

          <Box marginTop='m' flexDirection='row'>
            <Text marginRight='xs' variant='webSmall' color='textPrimary'>
              {t('models:saveProfile.skills')}
            </Text>
            <Icon name='AlertCircle' variant='s' color='textPrimary' />
          </Box>

          {skillIds && skillIds.length > 0 && (
            <Box mt='xs' flexDirection='row' flexWrap='wrap' mb='s'>
              <Tag
                label='+ Add Skills'
                labelVariant='labelSmall'
                variant='reversed'
                marginRight='xxs'
                padding='xxs'
                borderRadius='xxs'
                onPress={() => setSkillsModalOpen(true)}
              />
              {skillIds.map((item) => {
                const skill = skills.find((i) => i.id === item);
                if (!skill) return null;
                const { id: skillId, name: skillName } = skill;
                return (
                  <Tag
                    key={skillId}
                    label={skillName}
                    labelVariant='labelSmall'
                    variant='status'
                    marginRight='xxs'
                    padding='xxs'
                    borderRadius='xxs'
                    onDismiss={() => {
                      const newSkillIds = values.skillIds.filter(
                        (i) => i !== skillId
                      );
                      setTouched({ ...touched, skillIds: true });
                      setFieldValue('skillIds', newSkillIds);
                    }}
                    marginBottom='xs'
                  />
                );
              })}
            </Box>
          )}

          {!!skillIds && skillIds.length < 1 && (
            <Box mt='xs' width={92.22} flexDirection='row'>
              <Tag
                label='+ Add Skills'
                labelVariant='labelSmall'
                variant='reversed'
                marginRight='xxs'
                padding='xxs'
                borderRadius='xxs'
                onPress={() => setSkillsModalOpen(true)}
              />
              <Box flex={1} />
            </Box>
          )}
        </Box>

        <WebModal
          onClose={() => setLocationModalOpen(false)}
          visible={locationModalOpen}
          width={504}
          title='Location'
          height={512}>
          <Box flex={1} margin='m'>
            <LocationSearch
              isShowCancel={false}
              onClose={() => setLocationModalOpen(false)}
              onSelect={(location) => {
                setFieldValue('location', location);
                setTouched({ ...touched, location: true });
              }}
            />
          </Box>
        </WebModal>

        {skillsModalOpen && (
          <SkillSelect onClose={() => setSkillsModalOpen(false)} />
        )}

        <Box
          flex={1}
          flexDirection='row'
          justifyContent='space-between'
          paddingTop='listFooter'>
          <Box flex={1} marginRight='s'>
            <Button
              borderColor='black'
              variant='textweb'
              color='black'
              isSmall
              backgroundColor='white'
              disabled={isSubmitting}
              onPress={() => setIsEditProfileOpen(false)}
              accessibilityLabel={t('shared:cancel')}>
              {t('shared:cancel')}
            </Button>
          </Box>
          <Box flex={1}>{createButton(props)}</Box>
        </Box>
      </Box>
    );
  };

  return (
    <ScrollView>
      <Box
        maxWidth={Platform.OS === 'web' ? 700 : undefined}
        backgroundColor='white'
        marginTop='xl'>
        <KeyboardAwareScrollView style={{ flex: 1 }}>
          <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={saveProfileSchema}
            validateOnChange={true}
            validateOnBlur={false}>
            {(props) => ProfileComponent(props)}
          </Formik>
        </KeyboardAwareScrollView>
      </Box>
    </ScrollView>
  );
};

export default NewEditProfile;
